import { useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { ConversationView } from "@/interfaces/Chat";
import { useChat } from "@/contexts/ChatContext";
import {
  useAddChatMembersMutation,
  useGetChatMembersQuery,
  useRemoveChatMemberMutation,
  useRenameChatConversationMutation,
  useSetChatMemberRoleMutation,
} from "@/services/chat";
import { ChatUserPicker, chatError } from "./CreateConversationModal";
export default function GroupMembersModal({
  conversation,
  onClose,
}: {
  conversation: ConversationView;
  onClose: () => void;
}) {
  const { actorId } = useChat();
  const args = { actorId: actorId!, conversationId: conversation.id };
  const [q, setQ] = useState(""),
    [cursor, setCursor] = useState<string | undefined>(),
    [error, setError] = useState(""),
    [adding, setAdding] = useState(false),
    [selected, setSelected] = useState<string[]>([]),
    [name, setName] = useState(conversation.name),
    [busy, setBusy] = useState(false);
  const { currentData, isFetching, isError } = useGetChatMembersQuery({
    ...args,
    q,
    cursor,
  });
  const [add] = useAddChatMembersMutation(),
    [remove] = useRemoveChatMemberMutation(),
    [role] = useSetChatMemberRoleMutation(),
    [rename] = useRenameChatConversationMutation();
  const run = async (work: () => Promise<unknown>, done?: () => void) => {
    setBusy(true);
    setError("");
    try {
      await work();
      done?.();
    } catch (e) {
      setError(chatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      show
      onHide={onClose}
      centered
      scrollable
      aria-labelledby="chat-members-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="chat-members-title">Membres du groupe</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {conversation.canManage && (
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              run(() => rename({ ...args, name: name.trim() }).unwrap());
            }}
            className="mb-3"
          >
            <Form.Label htmlFor="chat-rename">Nom du groupe</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                id="chat-rename"
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Button
                type="submit"
                variant="outline-secondary"
                disabled={busy || !name.trim() || name === conversation.name}
              >
                Renommer
              </Button>
            </div>
          </Form>
        )}
        <Form.Label htmlFor="chat-members-search">
          Rechercher un membre
        </Form.Label>
        <Form.Control
          id="chat-members-search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setCursor(undefined);
          }}
        />
        {isError && (
          <Alert variant="danger">Impossible de charger les membres.</Alert>
        )}
        <ul className="chat-members" aria-busy={isFetching}>
          {currentData?.data.map((member, i) => (
            <li key={member.id || i}>
              <div>
                <strong>{member.name}</strong>
                <span>
                  {member.role === "admin" ? "Administrateur" : "Membre"}
                  {!member.active ? " · Inactif" : ""}
                </span>
              </div>
              {conversation.canManage && member.id && (
                <div className="chat-member-actions">
                  <Button
                    size="sm"
                    variant="link"
                    disabled={busy || !member.active}
                    aria-label={`${member.role === "admin" ? "Rétrograder" : "Promouvoir"} ${member.name}`}
                    onClick={() =>
                      run(() =>
                        role({
                          ...args,
                          userId: member.id!,
                          role: member.role === "admin" ? "member" : "admin",
                        }).unwrap(),
                      )
                    }
                  >
                    {member.role === "admin" ? "Rétrograder" : "Promouvoir"}
                  </Button>
                  {member.id !== actorId && (
                    <Button
                      size="sm"
                      variant="link"
                      disabled={busy}
                      aria-label={`Retirer ${member.name}`}
                      onClick={() =>
                        run(() =>
                          remove({ ...args, userId: member.id! }).unwrap(),
                        )
                      }
                    >
                      Retirer
                    </Button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
        {cursor && (
          <Button variant="link" onClick={() => setCursor(undefined)}>
            Début des membres
          </Button>
        )}
        {currentData?.nextCursor && (
          <Button
            variant="link"
            onClick={() => setCursor(currentData.nextCursor!)}
          >
            Membres suivants
          </Button>
        )}
        {conversation.canManage &&
          (adding ? (
            <>
              <ChatUserPicker selected={selected} onChange={setSelected} />
              <Button
                disabled={busy || !selected.length}
                onClick={() =>
                  run(
                    () => add({ ...args, userIds: selected }).unwrap(),
                    () => {
                      setAdding(false);
                      setSelected([]);
                      setCursor(undefined);
                    },
                  )
                }
              >
                Ajouter la sélection
              </Button>
            </>
          ) : (
            <Button variant="outline-primary" onClick={() => setAdding(true)}>
              Ajouter des membres
            </Button>
          ))}
      </Modal.Body>
      <Modal.Footer>
        {conversation.kind === "group" && conversation.canManage && !conversation.isMember && (
          <Button disabled={busy} onClick={() => run(() => add({ ...args, userIds: [actorId!] }).unwrap())}>
            Rejoindre le groupe
          </Button>
        )}
        {conversation.isMember && (
          <Button
            variant="outline-danger"
            disabled={busy}
            onClick={() =>
              run(() => remove({ ...args, userId: actorId! }).unwrap(), onClose)
            }
          >
            Quitter le groupe
          </Button>
        )}
        <Button variant="light" onClick={onClose}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
